/* ===========================================================================
   ne-review-modal.js

   Presents Judge.me's "write a review" flow as a centred modal instead of the
   inline form, which broke the two-column layout of the reviews section.

   The submission itself is NOT reimplemented: every value is written into
   Judge.me's own <form class="jdgm-form"> (kept in the DOM, moved off-screen by
   ne-judgeme.css) and its native submit button is clicked, so Judge.me's
   validation, CSRF/shop tokens, media upload and success handling all still run.

   Native field names, confirmed against the rendered markup:
     score               hidden input, written by clicking .jdgm-form__rating a.jdgm-star
     review_title        text
     review_body         textarea (required)
     media               file, multiple
     reviewer_name       text  (required for guests)
     reviewer_email      email (required for guests)
     .jdgm-submit-rev    input[type=submit]
   =========================================================================== */
(function () {
  'use strict';

  var MIN_BODY = 10;
  var MAX_PHOTOS = 5;
  var MAX_BYTES = 10 * 1024 * 1024;
  var OK_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  var modal = document.getElementById('ne-review-modal');
  if (!modal) return;

  var card     = modal.querySelector('.ne-rm__card');
  var bodyWrap = document.getElementById('ne-rm-body-wrap');
  var doneWrap = document.getElementById('ne-rm-done');
  var doneText = document.getElementById('ne-rm-done-text');
  var starsBox = document.getElementById('ne-rm-stars');
  var stars    = [].slice.call(starsBox.querySelectorAll('.ne-rm__star'));
  var titleEl  = document.getElementById('ne-rm-title');
  var textEl   = document.getElementById('ne-rm-text');
  var countEl  = document.getElementById('ne-rm-count');
  var dropEl   = document.getElementById('ne-rm-drop');
  var fileEl   = document.getElementById('ne-rm-file');
  var thumbsEl = document.getElementById('ne-rm-thumbs');
  var identity = document.getElementById('ne-rm-identity');
  var nameEl   = document.getElementById('ne-rm-name');
  var emailEl  = document.getElementById('ne-rm-email');
  var errorEl  = document.getElementById('ne-rm-error');
  var submitEl = document.getElementById('ne-rm-submit');

  var rating = 0;
  var photos = [];          // {file, url}
  var lastFocus = null;
  var busy = false;

  /* ---- the native Judge.me form ---------------------------------------- */

  function nativeForm(){ return document.querySelector('#customer-reviews form.jdgm-form'); }
  function nativeField(name){
    var f = nativeForm();
    return f ? f.querySelector('[name="' + name + '"]') : null;
  }

  // Judge.me renders the widget asynchronously; the link only exists after that.
  function nativeLink(){ return document.querySelector('#customer-reviews .jdgm-write-rev-link'); }

  // Opening the native form is what puts .jdgm-form in the DOM at all, so the
  // first open has to let Judge.me build it — off-screen, invisibly.
  function ensureNativeForm(cb){
    if (nativeForm()) { cb(nativeForm()); return; }
    var link = nativeLink();
    if (!link) { cb(null); return; }
    // HTMLElement.click() is a no-op while the same element is already
    // dispatching a click ("click in progress" flag), and we are called from
    // inside exactly that dispatch — so hand Judge.me a fresh event, out of
    // band, instead of calling .click() on the link.
    setTimeout(function(){
      link.setAttribute('data-ne-rm-internal', '1');
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      link.removeAttribute('data-ne-rm-internal');
    }, 0);
    var tries = 0;
    (function wait(){
      if (nativeForm()) { cb(nativeForm()); return; }
      if (tries++ > 60) { cb(null); return; }
      setTimeout(wait, 100);
    })();
  }

  /* ---- open / close ---------------------------------------------------- */

  function lockScroll(on){
    document.documentElement.classList.toggle('ne-rm-locked', on);
    document.body.classList.toggle('ne-rm-locked', on);
  }

  function open(){
    lastFocus = document.activeElement;
    modal.hidden = false;
    lockScroll(true);
    void modal.offsetWidth;                 // reflow, so the fade-in transition runs
    modal.classList.add('is-open');
    (stars[0] || titleEl).focus();
    document.addEventListener('keydown', onKeydown, true);

    // Judge.me builds .jdgm-form-wrapper lazily on the first click of its own
    // link and takes a second or two over it — do this last so nothing it does
    // can leave the modal half-opened.
    ensureNativeForm(function(form){
      if (!form) { showError('The review form could not be loaded. Please reload the page.'); return; }
      // guests must supply a name and an email — mirror whatever Judge.me asks for
      var needsIdentity = !!(nativeField('reviewer_name') || nativeField('reviewer_email'));
      identity.hidden = !needsIdentity;
      validate();
    });
  }

  // Only a real gesture inside the modal may close it. Judge.me re-renders the
  // review widget after a successful submit and the modal lives inside
  // #customer-reviews, so anything less strict risks the thank-you panel being
  // torn down before it has been read.
  function close(fromUser){
    if (busy) return;
    if (!fromUser) return;
    modal.classList.remove('is-open');
    modal.hidden = true;
    lockScroll(false);
    document.removeEventListener('keydown', onKeydown, true);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKeydown(e){
    if (e.key === 'Escape'){ e.preventDefault(); close(true); return; }
    if (e.key !== 'Tab') return;
    var f = [].slice.call(card.querySelectorAll(
      'button:not([disabled]), input:not([disabled]):not([type=file]), textarea, select, a[href]'
    )).filter(function(el){ return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first){ e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last){ e.preventDefault(); first.focus(); }
  }

  // Take over the rail's button (and any other Judge.me write-review link)
  document.addEventListener('click', function(e){
    var link = e.target.closest && e.target.closest('.jdgm-write-rev-link');
    if (!link || link.hasAttribute('data-ne-rm-internal')) return;
    e.preventDefault();
    e.stopPropagation();
    open();
  }, true);

  modal.addEventListener('click', function(e){
    if (e.target.closest('[data-ne-rm-close]')) { e.preventDefault(); close(true); }
  });

  /* ---- rating ---------------------------------------------------------- */

  function paintStars(value){
    stars.forEach(function(btn, i){
      btn.classList.toggle('is-on', i < value);
      btn.setAttribute('aria-checked', String(i + 1 === rating));
    });
  }

  stars.forEach(function(btn){
    var v = parseInt(btn.getAttribute('data-value'), 10);
    btn.addEventListener('click', function(){ rating = v; paintStars(rating); validate(); });
    btn.addEventListener('mouseenter', function(){ paintStars(v); });
    btn.addEventListener('focus', function(){ paintStars(v); });
  });
  starsBox.addEventListener('mouseleave', function(){ paintStars(rating); });
  starsBox.addEventListener('keydown', function(e){
    var i = stars.indexOf(document.activeElement);
    if (i < 0) return;
    var next = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? i + 1
             : e.key === 'ArrowLeft'  || e.key === 'ArrowDown' ? i - 1 : null;
    if (next === null) return;
    e.preventDefault();
    next = Math.max(0, Math.min(stars.length - 1, next));
    stars[next].focus();
    stars[next].click();
  });

  /* ---- photos ---------------------------------------------------------- */

  dropEl.addEventListener('click', function(){ fileEl.click(); });
  fileEl.addEventListener('change', function(){ addFiles(fileEl.files); fileEl.value = ''; });

  ['dragover', 'dragenter'].forEach(function(t){
    dropEl.addEventListener(t, function(e){ e.preventDefault(); dropEl.classList.add('is-over'); });
  });
  ['dragleave', 'drop'].forEach(function(t){
    dropEl.addEventListener(t, function(e){ e.preventDefault(); dropEl.classList.remove('is-over'); });
  });
  dropEl.addEventListener('drop', function(e){
    if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
  });

  function addFiles(list){
    var rejected = [];
    [].slice.call(list).forEach(function(file){
      if (photos.length >= MAX_PHOTOS) { rejected.push('Up to ' + MAX_PHOTOS + ' photos.'); return; }
      if (OK_TYPES.indexOf(file.type) < 0) { rejected.push(file.name + ' is not a JPEG or PNG.'); return; }
      if (file.size > MAX_BYTES) { rejected.push(file.name + ' is over 10MB.'); return; }
      photos.push({ file: file, url: URL.createObjectURL(file) });
    });
    if (rejected.length) showError(rejected[0]); else hideError();
    paintThumbs();
  }

  function paintThumbs(){
    thumbsEl.innerHTML = '';
    photos.forEach(function(p, i){
      var fig = document.createElement('figure');
      fig.className = 'ne-rm__thumb';
      var img = document.createElement('img');
      img.src = p.url;
      img.alt = '';
      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'ne-rm__thumb-x';
      x.setAttribute('aria-label', 'Remove photo ' + (i + 1));
      x.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>';
      x.addEventListener('click', function(){
        URL.revokeObjectURL(p.url);
        photos.splice(i, 1);
        paintThumbs();
      });
      fig.appendChild(img);
      fig.appendChild(x);
      thumbsEl.appendChild(fig);
    });
    dropEl.disabled = photos.length >= MAX_PHOTOS;
  }

  /* ---- validation ------------------------------------------------------ */

  function validate(){
    var body = textEl.value.trim();
    var need = MIN_BODY - body.length;
    countEl.textContent = need > 0
      ? 'Minimum 10 characters — ' + need + ' to go.'
      : body.length + ' characters.';
    countEl.classList.toggle('is-short', need > 0);

    var ok = rating > 0 && titleEl.value.trim().length > 0 && body.length >= MIN_BODY;
    if (!identity.hidden) {
      ok = ok && nameEl.value.trim().length > 0 && /.+@.+\..+/.test(emailEl.value.trim());
    }
    submitEl.disabled = !ok;
    return ok;
  }

  [titleEl, textEl, nameEl, emailEl].forEach(function(el){
    el.addEventListener('input', validate);
  });
  validate();

  function showError(msg){ errorEl.textContent = msg; errorEl.hidden = false; }
  function hideError(){ errorEl.hidden = true; }

  /* ---- submit through Judge.me's own form ------------------------------ */

  function setValue(el, value){
    if (!el) return;
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setNativeRating(form, value){
    var list = form.querySelectorAll('.jdgm-form__rating .jdgm-star');
    if (list[value - 1]) list[value - 1].click();
    var score = form.querySelector('[name="score"]');
    if (score && String(score.value) !== String(value)) setValue(score, value);
  }

  // Judge.me does not submit input.files: its change handler moves the files
  // into its own preview list (.jdgm-picture-fieldset__box--preview) and clears
  // the input. So hand it the files through a change event — and first drop any
  // previews left over from an earlier attempt, or they would be posted twice.
  function clearNativeFiles(form){
    var stale = form.querySelectorAll('.jdgm-picture-fieldset__box--preview .jdgm-picture-fieldset__delete');
    [].slice.call(stale).forEach(function(btn){ btn.click(); });
  }

  function setNativeFiles(form){
    clearNativeFiles(form);
    var input = form.querySelector('input[type="file"][name="media"]');
    if (!input || !photos.length) return;
    if (typeof DataTransfer === 'undefined') return;
    var dt = new DataTransfer();
    photos.forEach(function(p){ dt.items.add(p.file); });
    input.files = dt.files;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function nativePhotoCount(form){
    return form.querySelectorAll('.jdgm-picture-fieldset__box--preview').length;
  }

  // Judge.me uploads each file to its own storage in the background and refuses
  // the submit until that finishes ("Please wait for your picture or video to
  // finish uploading"). Each preview keeps a spinner wrapper that is hidden and
  // a progress text that reads 100% once its upload lands.
  function nativeMediaReady(form){
    var boxes = form.querySelectorAll('.jdgm-picture-fieldset__box--preview');
    if (boxes.length < photos.length) return false;
    for (var i = 0; i < boxes.length; i++){
      var spinner = boxes[i].querySelector('.jdgm-picture-fieldset__spinner-wrapper');
      var pct = boxes[i].querySelector('.jdgm-picture-fieldset__progress-text');
      var spinning = spinner && spinner.offsetParent !== null;
      var done = !pct || (pct.textContent || '').trim() === '100%';
      if (spinning || !done) return false;
    }
    return true;
  }

  submitEl.addEventListener('click', function(){
    if (!validate() || busy) return;
    var form = nativeForm();
    if (!form) { showError('The review form could not be loaded. Please reload the page.'); return; }

    hideError();
    busy = true;
    submitEl.classList.add('is-busy');
    submitEl.disabled = true;
    submitEl.textContent = 'Submitting…';

    setNativeRating(form, rating);
    setValue(form.querySelector('[name="review_title"]'), titleEl.value.trim());
    setValue(form.querySelector('[name="review_body"]'), textEl.value.trim());
    if (!identity.hidden) {
      setValue(form.querySelector('[name="reviewer_name"]'), nameEl.value.trim());
      setValue(form.querySelector('[name="reviewer_email"]'), emailEl.value.trim());
    }
    setNativeFiles(form);

    var btn = form.querySelector('.jdgm-submit-rev');
    if (!btn) { fail('Judge.me’s submit control is missing.'); return; }
    // clear any error Judge.me printed on a previous attempt, so the outcome
    // watcher cannot read a stale message as this submission's result
    var stale = form.querySelector('.jdgm-form__submit-error');
    if (stale && stale.parentNode) stale.parentNode.removeChild(stale);

    var waited = 0;
    (function whenMediaReady(){
      if (!photos.length || nativeMediaReady(form) || waited > 30000) {
        if (photos.length && !nativeMediaReady(form)) {
          fail('Your photos are still uploading. Please try again in a moment.');
          return;
        }
        btn.click();
        watchOutcome(form);
        return;
      }
      if (waited === 0 || waited % 2400 === 0) {
        submitEl.textContent = photos.length ? 'Uploading photos…' : 'Submitting…';
      }
      waited += 120;
      setTimeout(whenMediaReady, 120);
    })();
  });

  function fail(msg){
    busy = false;
    submitEl.classList.remove('is-busy');
    submitEl.textContent = 'Submit review';
    validate();
    showError(msg);
  }

  function succeed(msg){
    busy = false;
    submitEl.classList.remove('is-busy');
    submitEl.textContent = 'Submit review';
    if (msg) doneText.textContent = msg;
    bodyWrap.hidden = true;
    doneWrap.hidden = false;
    photos.forEach(function(p){ URL.revokeObjectURL(p.url); });
    photos = [];
    doneWrap.setAttribute('tabindex', '-1');
    doneWrap.focus();
  }

  // Judge.me does not expose a callback, so watch the widget for the outcome:
  // an error message it prints, or the form clearing / a thank-you appearing.
  function watchOutcome(form){
    var host = document.getElementById('customer-reviews');
    var started = Date.now();
    var seen = false;

    function errorText(){
      var node = host.querySelector('.jdgm-form__submit-error, .jdgm-form .jdgm-err, .jdgm-form [class*="error"], .jdgm-notification--error');
      if (node && node.offsetParent !== null) {
        var t = (node.textContent || '').trim();
        if (t) return t;
      }
      return null;
    }
    function successNode(){
      return host.querySelector(
        '.jdgm-form__msg, .jdgm-notification:not(.jdgm-notification--error), .jdgm-rev-widg__message, [class*="jdgm"][class*="success"], [class*="jdgm"][class*="thank"]'
      );
    }

    (function poll(){
      if (seen) return;
      var err = errorText();
      if (err) { seen = true; fail(err); return; }

      var ok = successNode();
      var cleared = form.querySelector('[name="review_body"]') &&
                    form.querySelector('[name="review_body"]').value === '' &&
                    Date.now() - started > 1200;
      if (ok || cleared) {
        seen = true;
        succeed(ok && (ok.textContent || '').trim() ? (ok.textContent || '').trim() : null);
        return;
      }
      if (Date.now() - started > 12000) {
        // Judge.me neither errored nor confirmed within 12s — the POST is on its
        // way; say so honestly rather than claiming it is published.
        seen = true;
        succeed('Your review has been sent. It will appear here once Judge.me publishes it.');
        return;
      }
      setTimeout(poll, 250);
    })();
  }
})();
